import { useCallback, useMemo, useRef, useState } from "react"
import {
    DURATION_PER_PERCENT_BY_STEP_PERCENT,
    GenericJob,
    InfraredProcessingMode,
    JobStatusType,
    JobTypes,
    JobTypesWithProgressSupport,
    OrthoProcessingMode,
} from "@nova/constants"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import { InfraredJob, OrthoJob, PDFJob, SatelliteJob } from "@nova/protobuf"

export const useJobProgress = ({
    job,
    type,
    totalDurationSeconds,
}: {
    job?: InfraredJob | OrthoJob | PDFJob | SatelliteJob | GenericJob
    type: JobTypesWithProgressSupport | undefined
    // Overrides the type's static schedule for jobs whose duration depends on a per-instance value.
    totalDurationSeconds?: number
}) => {
    // hack because this type is hardcoded in the multifile upload spec
    if (job && "processingMode" in job) {
        if (job.processingMode === OrthoProcessingMode.Urban) {
            type = JobTypes.OrthoUrban
        } else if (job.processingMode === OrthoProcessingMode.Rapid) {
            type = JobTypes.OrthoRapid
        } else if (job.processingMode === InfraredProcessingMode.Rapid) {
            type = JobTypes.InfraredRapid
        }
    }
    const clock = useRef<NodeJS.Timeout>(undefined)
    const jobRef = useRef(job)

    const [durations, nextStepPercents] = useMemo(() => {
        if (totalDurationSeconds) return [{ 0: totalDurationSeconds / 99 }, { 0: 100 }]
        if (!type) return [{}, {}]
        const durations = DURATION_PER_PERCENT_BY_STEP_PERCENT[type]
        const percents = Object.keys(durations).map(Number)
        const nextStepPercents = percents.reduce(
            (acc, cur, i) => {
                acc[cur] = percents[i + 1] || 100
                return acc
            },
            {} as Record<number, number>
        )

        return [durations, nextStepPercents]
    }, [type, totalDurationSeconds])

    const calculateProgress = useCallback(() => {
        if (!jobRef.current?.updatedAt && !jobRef.current?.createdAt)
            return jobRef.current?.progress ?? 0

        const percent = jobRef.current.progress || 0
        const step = Object.keys(durations)
            .map(Number)
            .sort((a, b) => a - b)
            .reduce((acc, p) => (p <= percent ? p : acc), 0)

        const durationPerPercent = durations[step]
        const nextStepPercent = nextStepPercents[step]
        if (!durationPerPercent) {
            console.error(`No duration defined for progress value: ${percent}.`)
            return percent
        }

        const secondsSinceUpdate = Math.floor(
            (Date.now() -
                dateFromNumber(jobRef.current.updatedAt || jobRef.current.createdAt).getTime()) /
                1000
        )

        const calculatedPercent = Math.floor(secondsSinceUpdate / durationPerPercent) + percent

        // Set progress to be between X% and Y% based on how long the job has been queued.
        const newProgress = Math.min(nextStepPercent - 1, Math.max(percent, calculatedPercent))
        return newProgress
    }, [durations, nextStepPercents])

    const [progress, setProgress] = useState<number>(calculateProgress())

    useEffectUnsafe(() => {
        const isActive =
            !!job &&
            (job.status === JobStatusType.QUEUED ||
                job.status === JobStatusType.STARTED ||
                (job.status === JobStatusType.LIVE && !!job.progress))
        if (!isActive || (job?.progress && job.progress >= 99)) {
            jobRef.current = undefined
            return
        }
        jobRef.current = job
        setProgress(calculateProgress())
        const calculateAndSetProgress = () => {
            const newProgress = calculateProgress()
            setProgress(newProgress)
        }
        clock.current = setInterval(calculateAndSetProgress, 1000)
        return () => {
            if (clock.current) clearInterval(clock.current)
        }
    }, [job])

    return progress
}
